import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ApiServices from '../../shared/ApiServices/ApiServices';
import { Dialog } from '../../shared/custom-ui';
import { V2Button } from '../../shared/custom-ui/v2-components';
import DragAndDropFiles from '../../shared/components/drag-drop-files/DragDropFiles';

type MedicalDocProps = {
    isOpen: boolean;
    patientId: string | null;
    onClose: () => void;
    onUploadSuccess: () => void;
};

const AddMedicalDoc: React.FC<MedicalDocProps> = ({
    isOpen,
    patientId,
    onClose,
    onUploadSuccess,
}) => {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);

    // Reset when dialog closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedFiles([]);
            setUploading(false);
        }
    }, [isOpen]);

    // Handle file selection
    const handleFileChange = (files: File[]) => {
        if (files.length > 0) setSelectedFiles(files);
        else toast.error('Please select valid files.');
    };

    // Upload all selected files
    const handleUpload = async () => {
        if (!selectedFiles.length) return toast('Please select files to upload.');
        if (!patientId) return toast('Patient ID is missing.');

        setUploading(true);
        try {
            for (const file of selectedFiles) {
                const mimeType = file.type;
                const extension = file.name.split('.').pop() || '';

                // Step 1: Get presigned URL
                const preSigned = await ApiServices.getMedicalDocPresignedUrl(
                    patientId,
                    extension,
                    mimeType
                );
                const { url, fields } = preSigned as {
                    url: string;
                    fields: Record<string, string>;
                };

                if (!url || !fields) throw new Error('Invalid pre-signed URL response.');

                // Step 2: Build form data
                const formData = new FormData();
                Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
                formData.append('file', file);

                // Step 3: Upload file
                await ApiServices.uploadMedicalDoc(url, formData);
            }

            toast.success('Files uploaded successfully!');
            setSelectedFiles([]);
            onUploadSuccess();
            onClose();
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Failed to upload the files. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            size="medium"
            title="Upload Medical Documents"
            showCloseButton
        >
            <div className="flex flex-col gap-5">
                {/* Upload area */}
                <div className="flex-center h-[220px] max-h-[220px]">
                    {uploading ? (
                        <div className="flex items-center justify-center w-full text-gray-600">
                            Uploading...
                        </div>
                    ) : (
                        <DragAndDropFiles
                            handleChange={handleFileChange}
                            name="files"
                            multiple
                            accept="application/pdf,image/*"
                        />
                    )}
                </div>

                {/* Selected file count */}
                {selectedFiles.length > 0 && !uploading && (
                    <p className="text-sm text-gray-700 text-center">
                        Selected files: <span className="font-semibold">{selectedFiles.length}</span>
                    </p>
                )}

                {/* Footer actions */}
                <div className="flex justify-end gap-3 pt-2 border-t">
                    <V2Button
                        variant="danger"
                        onClick={onClose}
                        disabled={uploading}
                    >
                        Cancel
                    </V2Button>
                    <V2Button
                        variant="primary"
                        onClick={handleUpload}
                        loading={uploading}
                        disabled={uploading || selectedFiles.length === 0}
                    >
                        Upload
                    </V2Button>
                </div>
            </div>
        </Dialog>
    );
};

export default AddMedicalDoc;
